import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import { isCashierUser } from '../utils/roleUtils';
import { Capacitor } from '@capacitor/core';

const LoginPage = () => {
  const platform = Capacitor?.getPlatform?.() || 'web';
  const isNative = platform !== 'web';

  const [userId, setUserId] = useState('');
  const [pin, setPin] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaChallengeToken, setMfaChallengeToken] = useState('');
  const [mfaEmail, setMfaEmail] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);

  const [error, setError] = useState('');
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetStatus, setResetStatus] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const { login, verifyMfa } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (!isNative && mfaRequired) {
        const loggedInUser = await verifyMfa({
          challengeToken: mfaChallengeToken,
          code: mfaCode.trim(),
        });
        navigate(isCashierUser(loggedInUser) ? '/checkout' : '/');
        return;
      }

      const credentials = isNative ? { userId, pin } : { email, password };
      const loginResponse = await login(credentials);
      if (!isNative && loginResponse?.mfaRequired) {
        setMfaRequired(true);
        setMfaChallengeToken(loginResponse.challengeToken || '');
        setMfaEmail(loginResponse.email || email);
        setMfaCode('');
        return;
      }
      const loggedInUser = loginResponse;
      navigate(isCashierUser(loggedInUser) ? '/checkout' : '/');
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetError('');
    setResetStatus('');
    if (!resetEmail.trim()) {
      setResetError('Please enter your email.');
      return;
    }
    try {
      setResetLoading(true);
      await authService.requestPasswordReset(resetEmail.trim());
      setResetStatus('If an account exists for that email, a reset link has been sent.');
      setResetEmail('');
    } catch (err) {
      setResetError(err.response?.data?.message || 'Unable to process request.');
    } finally {
      setResetLoading(false);
    }
  };

  const renderNativeForm = () => (
    <>
      <div className="mb-4">
        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="userId">
          User ID (numeric)
        </label>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          id="userId"
          autoComplete="off"
          maxLength={20}
          value={userId}
          onChange={(e) => setUserId(e.target.value.replace(/\D+/g, ''))}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          required
        />
      </div>
      <div className="mb-2">
        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="pin">
          6-digit PIN
        </label>
        <input
          type="password"
          inputMode="numeric"
          pattern="[0-9]{6}"
          autoComplete="off"
          id="pin"
          maxLength={6}
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D+/g, '').slice(0, 6))}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 mb-3 leading-tight focus:outline-none focus:shadow-outline"
          style={{
            WebkitTextSecurity: 'disc',
            MozTextSecurity: 'disc',
            textSecurity: 'disc',
          }}
          required
        />
      </div>
    </>
  );

  const renderWebForm = () => (
    <>
      {!mfaRequired && (
        <>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="email">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              required
            />
          </div>
          <div className="mb-2">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="password">
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 mb-3 leading-tight focus:outline-none focus:shadow-outline"
              required
            />
          </div>
          <div className="flex justify-end mb-4">
            <button
              type="button"
              onClick={() => setShowResetModal(true)}
              className="text-sm text-primary hover:text-primary-dark"
            >
              Forgot password?
            </button>
          </div>
        </>
      )}
      {mfaRequired && (
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="mfaCode">
            Verification Code
          </label>
          <p className="text-xs text-gray-600 mb-2">
            Enter the 6-digit code sent to <span className="font-semibold">{mfaEmail || email}</span>.
          </p>
          <input
            type="text"
            id="mfaCode"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value.replace(/\D+/g, '').slice(0, 6))}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 mb-3 leading-tight focus:outline-none focus:shadow-outline"
            required
          />
          <button
            type="button"
            onClick={() => {
              setMfaRequired(false);
              setMfaChallengeToken('');
              setMfaCode('');
            }}
            className="text-sm text-gray-600 hover:text-gray-800"
          >
            Back to email/password
          </button>
        </div>
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">POS Login</h2>
        {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}
        <form onSubmit={handleSubmit}>
          {isNative ? renderNativeForm() : renderWebForm()}
          <div className="flex items-center justify-between">
            <button
              type="submit"
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline w-full"
            >
              {mfaRequired ? 'Verify Code' : 'Sign In'}
            </button>
          </div>
        </form>
      </div>
      {showResetModal && !isNative && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">Reset Password</h3>
              <button
                type="button"
                onClick={() => {
                  setShowResetModal(false);
                  setResetEmail('');
                  setResetStatus('');
                  setResetError('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleForgotPassword}>
              <label className="block text-gray-700 text-sm font-medium mb-2" htmlFor="resetEmail">
                Enter your email address
              </label>
              <input
                type="email"
                id="resetEmail"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                className="w-full border rounded px-3 py-2 mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              {resetError && <p className="text-sm text-red-600 mb-2">{resetError}</p>}
              {resetStatus && <p className="text-sm text-green-600 mb-2">{resetStatus}</p>}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowResetModal(false);
                    setResetEmail('');
                    setResetStatus('');
                    setResetError('');
                  }}
                  className="px-4 py-2 rounded border border-gray-300 text-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={resetLoading}
                  className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-50"
                >
                  {resetLoading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
